import { useScrollReveal, useParallax } from '@/hooks/useScrollAnimations';
import { Target, Eye, MapPin } from 'lucide-react';

const About = () => {
  const { ref, isVisible } = useScrollReveal();
  const offset = useParallax();

  return (
    <section id="about" className="relative py-24 overflow-hidden">
      {/* Parallax strip */}
      <div
        className="absolute inset-0 bg-secondary opacity-5"
        style={{ transform: `translateY(${(offset - 1500) * 0.1}px)` }}
      />

      <div ref={ref} className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-16'}`}>
            <span className="text-primary font-semibold text-sm uppercase tracking-widest">About Us</span>
            <h2 className="text-3xl md:text-5xl font-bold font-display mt-3 mb-6 text-foreground">
              Empowering Brands,<br />
              <span className="text-gradient">Inspiring Futures</span>
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              OyinBatfo Fashion/Branding Solutions is a premier branding company dedicated to
              empowering schools, corporates, and individuals with innovative, high-quality
              branding solutions. Since 2012, we have been at the forefront of transforming
              visions into tangible brand experiences.
            </p>

            <div className="space-y-6">
              {[
                { icon: Eye, title: 'Vision', text: 'To be the leading branding partner for schools and corporates across Nigeria and beyond.' },
                { icon: Target, title: 'Mission', text: 'To deliver exceptional branding solutions that elevate identity, foster pride, and drive impact.' },
                { icon: MapPin, title: 'Presence', text: 'Operating from Lagos and Ilorin, serving clients nationwide.' },
              ].map((item, i) => (
                <div key={item.title} className="flex gap-4" style={{ transitionDelay: `${300 + i * 150}ms` }}>
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold font-display text-foreground">{item.title}</h3>
                    <p className="text-muted-foreground text-sm">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Product Collage */}
          <div className={`relative transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-16'}`}>
            <div className="aspect-square rounded-3xl bg-gradient-to-br from-primary/20 to-primary-glow/10 border border-primary/20 relative overflow-hidden p-4">
              {/* Animated collage columns */}
              <div className="absolute inset-3 rounded-2xl overflow-hidden flex gap-3">
                {/* Column 1 - scrolls up */}
                <div className="flex-1 flex flex-col gap-3 animate-[collage-up_20s_linear_infinite]">
                  {[
                    '/images/products/embroidery-towel.jpg',
                    '/images/products/branded-apron.jpg',
                    '/images/products/church-stole.jpg',
                    '/images/products/custom-mug.jpg',
                    '/images/products/branded-cap.jpg',
                    '/images/products/gold-vestment.jpg',
                    '/images/products/embroidery-towel.jpg',
                    '/images/products/branded-apron.jpg',
                    '/images/products/church-stole.jpg',
                    '/images/products/custom-mug.jpg',
                  ].map((src, i) => (
                    <img key={i} src={src} alt="Product" className="w-full rounded-lg object-cover aspect-square flex-shrink-0" />
                  ))}
                </div>
                {/* Column 2 - scrolls down */}
                <div className="flex-1 flex flex-col gap-3 animate-[collage-down_25s_linear_infinite]">
                  {[
                    '/images/products/sublimation-jersey.jpg',
                    '/images/products/nurtw-polo.jpg',
                    '/images/products/custom-pillow.jpg',
                    '/images/products/school-sweater.jpg',
                    '/images/products/safety-vest.jpg',
                    '/images/products/branded-jersey.jpg',
                    '/images/products/sublimation-jersey.jpg',
                    '/images/products/nurtw-polo.jpg',
                    '/images/products/custom-pillow.jpg',
                    '/images/products/school-sweater.jpg',
                  ].map((src, i) => (
                    <img key={i} src={src} alt="Product" className="w-full rounded-lg object-cover aspect-square flex-shrink-0" />
                  ))}
                </div>
                {/* Column 3 - scrolls up slower */}
                <div className="flex-1 flex flex-col gap-3 animate-[collage-up_30s_linear_infinite]">
                  {[
                    '/images/products/purple-vestment.jpg',
                    '/images/products/memorial-jersey.jpg',
                    '/images/products/custom-mugs-set.jpg',
                    '/images/products/printed-tshirts.jpg',
                    '/images/products/lihoc-polo-front.jpg',
                    '/images/products/embroidered-cap.jpg',
                    '/images/products/purple-vestment.jpg',
                    '/images/products/memorial-jersey.jpg',
                    '/images/products/custom-mugs-set.jpg',
                    '/images/products/printed-tshirts.jpg',
                  ].map((src, i) => (
                    <img key={i} src={src} alt="Product" className="w-full rounded-lg object-cover aspect-square flex-shrink-0" />
                  ))}
                </div>
              </div>
              {/* Gradient overlays for seamless loop feel */}
              <div className="absolute inset-3 rounded-2xl pointer-events-none bg-gradient-to-b from-primary/10 via-transparent to-primary/10 z-10" />
              {/* Decorative corners */}
              <div className="absolute top-4 left-4 w-12 h-12 border-t-2 border-l-2 border-primary rounded-tl-xl z-20" />
              <div className="absolute bottom-4 right-4 w-12 h-12 border-b-2 border-r-2 border-primary rounded-br-xl z-20" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
